<template>
  <div class="relative" @keydown.esc.stop="isOpen = false">
    <!-- City Button - Trigger -->
    <UButton
      type="button"
      :color="buttonColor"
      :variant="buttonVariant"
      :size="buttonSize"
      class="justify-start text-base font-medium min-h-11"
      :class="buttonClass"
      :title="selectedCity.name"
      aria-label="Shehar tabdeel karein"
      :aria-expanded="isOpen"
      :aria-controls="cityMenuId"
      @click="isOpen = !isOpen"
    >
      <template #leading>
        <UIcon name="i-heroicons-map-pin" :class="iconClass" />
      </template>

      <span class="whitespace-normal">
        {{ selectedCity.name }}
      </span>

      <!-- Chevron icon that rotates -->
      <template #trailing>
        <UIcon
          name="i-heroicons-chevron-down-20-solid"
          class="transition-transform"
          :class="[chevronClass, { 'rotate-180': isOpen }]"
        />
      </template>
    </UButton>

    <!-- Dropdown Menu -->
    <Transition
      enter-active-class="transition ease-out duration-100"
      enter-from-class="transform opacity-0 scale-95"
      enter-to-class="transform opacity-100 scale-100"
      leave-active-class="transition ease-in duration-75"
      leave-from-class="transform opacity-100 scale-100"
      leave-to-class="transform opacity-0 scale-95"
    >
      <div
        v-if="isOpen"
        :id="cityMenuId"
        role="listbox"
        aria-label="City options"
        class="absolute left-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg z-10 border border-gray-200 dark:border-gray-700 overflow-hidden"
        @click.stop
      >
        <!-- Search input (optional but nice) -->
        <div class="px-3 py-2 border-b border-gray-200 dark:border-gray-700">
          <UInput
            v-model="searchQuery"
            type="text"
            placeholder="Shehar dhundhe..."
            size="sm"
            icon="i-heroicons-magnifying-glass-20-solid"
            @click.stop
          />
        </div>

        <!-- City List -->
        <div
          class="max-h-60 overflow-y-auto divide-y divide-gray-200 dark:divide-gray-700"
        >
          <button
            v-for="city in filteredCities"
            :key="city.key"
            @click="selectCity(city)"
            role="option"
            :aria-selected="selectedCity.key === city.key"
            class="w-full min-h-11 px-4 py-2.5 text-left text-sm transition-colors hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between group"
            :class="[
              selectedCity.key === city.key
                ? 'bg-blue-50 dark:bg-blue-900/20'
                : '',
            ]"
          >
            <span
              :class="[
                selectedCity.key === city.key
                  ? 'text-blue-600 dark:text-blue-400 font-semibold'
                  : 'text-gray-900 dark:text-gray-100',
              ]"
            >
              {{ city.name }}
            </span>

            <!-- Checkmark for selected -->
            <UIcon
              v-if="selectedCity.key === city.key"
              name="i-heroicons-check-20-solid"
              class="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0"
            />
          </button>

          <!-- Empty state -->
          <div
            v-if="filteredCities.length === 0"
            class="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400"
          >
            Koi shehar nahi mila
          </div>
        </div>
      </div>
    </Transition>

    <!-- Click outside to close -->
    <div v-if="isOpen" class="fixed inset-0 z-0" @click="isOpen = false" />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";

type CityOption = { key: string; name: string };

const props = withDefaults(
  defineProps<{
    cities: CityOption[];
    selectedCityKey: string;
    buttonVariant?: "solid" | "outline" | "ghost" | "soft" | "link";
    buttonColor?: string;
    buttonSize?: "xs" | "sm" | "md" | "lg";
    buttonClass?: string;
    iconClass?: string;
    chevronClass?: string;
  }>(),
  {
    buttonVariant: "ghost",
    buttonColor: "gray",
    buttonSize: "sm",
    buttonClass: "",
    iconClass: "w-4 h-4",
    chevronClass: "w-4 h-4",
  },
);

const emit = defineEmits<{
  (e: "select", city: CityOption): void;
  (e: "open-change", isOpen: boolean): void;
}>();

const isOpen = ref(false);
const searchQuery = ref("");
const cityMenuId = "city-selector-menu";

const selectedCity = computed(() => {
  return (
    props.cities.find((c) => c.key === props.selectedCityKey) || props.cities[0]
  );
});

const filteredCities = computed(() => {
  if (!searchQuery.value) return props.cities;

  const query = searchQuery.value.toLowerCase();
  return props.cities.filter((city) => city.name.toLowerCase().includes(query));
});

const selectCity = (city: CityOption) => {
  // Only emit if actually selecting a different city
  if (city.key !== props.selectedCityKey) {
    emit("select", city);
  }
  isOpen.value = false;
  searchQuery.value = "";
};

watch(isOpen, (next) => {
  emit("open-change", next);
});
</script>
